import { makeClient } from '@spree/storefront-api-v2-sdk';
import { logErrorToSentry } from '../helpers/helpers';

import {
  setupRequestSampleModal,
  getSelectedSampleTypeProperties,
} from './dom';

import { fireAdobeAnalyticsEvent } from '../helpers/adobe-analytics';

const setGlobalTokens = (orderToken, oauthToken) => {
  const result = {
    orderToken,
    oauthToken,
  };

  window.SpreeAPI = { ...window.SpreeAPI, ...result };

  return result;
};

function sendAnalyticsEvent(sampleType) {
  const eInfo = window.location.pathname.split('/');
  const label = eInfo[3] ? `${eInfo[2]}-${eInfo[3]}` : eInfo[2];
  // gtag is only defined in production
  if (typeof gtag === 'function') {
    gtag('event', sampleType, {
      event_category: 'Order Sample',
      event_label: label,
    });
  }
}

export const getProductAnalyticsName = async variantId => {
  if (!window?.SpreeAPI?.host) return;

  const url = `${window.SpreeAPI.host}/api/v2/storefront/products/${variantId}?include=product_properties`;
  const headers = { Accept: 'application/vnd.api+json' };

  try {
    const response = await fetch(url, { method: 'GET', headers });
    const data = await response.json();

    return Array.isArray(data?.included)
      ? data.included.find(
          item =>
            item?.type === 'product_property' &&
            item?.attributes?.name === 'analytics_name'
        )?.attributes?.value
      : undefined;
  } catch (error) {
    logErrorToSentry?.(error);
    return undefined;
  }
};

const sendAdobeAddToCart = async variantId => {
  const productAnalyticsName = await getProductAnalyticsName(variantId);
  if (productAnalyticsName) {
    const cartId = window.analytics?.cart?.id || null;
    const eventData = {
      type: 'cart:add',
      detail: {
        products: productAnalyticsName,
        ...(cartId && { cart: { id: cartId } }),
      },
    };
    fireAdobeAnalyticsEvent(eventData);
  } else {
    console.warn('Missing analytics_name for variant:', variantId);
  }
};

const getTokens = async () => {
  const response = await fetch(`${window.SpreeAPI.host}/api_tokens`, {
    credentials: 'include',
  });
  const json = await response.json();

  return setGlobalTokens(json.order_token, json.oauth_token);
};

export const fetchCart = async () => {
  const tokens = await getTokens();

  if (!tokens.orderToken) return;

  const client = makeClient({ host: window.SpreeAPI.host });
  const response = await client.cart.show({ orderToken: tokens.orderToken });

  if (response.isSuccess()) return response.success();
  else console.warn(response.fail().summary);
};

export const checkProductAvailability = async productId => {
  const client = makeClient({ host: window.SpreeAPI.host });

  return await client.products.show(productId, {
    include: 'product_properties',
  });
};

export const addSelectedSampleTypeToCart = async quantity => {
  const selectedSampleTypeProperties = getSelectedSampleTypeProperties(),
    analyticsSampleType =
      selectedSampleTypeProperties.cartModalInfo.sampleType
        .toLowerCase()
        .replace(' sample', '') ||
      selectedSampleTypeProperties.analyticsTracker;

  return await addToCart(
    selectedSampleTypeProperties.id,
    quantity,
    analyticsSampleType
  );
};

export const addToCart = async (variantId, quantity, analyticsSampleType) => {
  let orderToken;
  const client = makeClient({ host: window.SpreeAPI.host });

  if (!window.SpreeAPI.orderToken) {
    const cartResponse = await fetch(`${window.SpreeAPI.host}/ensure_cart`, {
      method: 'POST',
      credentials: 'include',
    });

    const cart = await cartResponse.json();

    setGlobalTokens(cart.token);
    orderToken = cart.token;
  } else {
    orderToken = window.SpreeAPI.orderToken;
  }

  const response = await client.cart.addItem(
    { orderToken },
    {
      variant_id: variantId,
      quantity,
    }
  );

  if (response.isSuccess()) {
    MicroModal.show('modal-request-sample');
    setupRequestSampleModal();
    sendAdobeAddToCart(variantId);
    // Todo: uncomment this after testing confirmation from Alpha
    // sendAnalyticsEvent(analyticsSampleType);
    return response.success().data.attributes;
  } else {
    const error = new Error(response.fail().summary);
    window.Sentry.captureException(error);
    alert(response.fail().summary);
  }
};

export const freeSampleCounts = async (variantId = '') => {
  let orderToken;

  if (!window.SpreeAPI.orderToken) {
    const cartResponse = await fetch(`${window.SpreeAPI.host}/ensure_cart`, {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `X-Spree-Order-Token ${orderToken}` },
    });

    const cart = await cartResponse.json();

    setGlobalTokens(cart.token);
    orderToken = cart.token;
  } else {
    orderToken = window.SpreeAPI.orderToken;
  }

  const freeSampleCountUrl = `${window.SpreeAPI.host}/api/v2/storefront/free_sample_counts?variant_id=${variantId}`;
  const init = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Spree-Order-Token': orderToken,
    },
  };
  return await fetch(freeSampleCountUrl, init)
    .then(response => {
      if (response.ok) {
        return response.json();
      }
    })
    .then(json => {
      return json;
    })
    .catch(error => {
      logErrorToSentry(error);
    });
};

export const fetchAccountLinks = async () => {
  return await fetch(`${window.SpreeAPI.host}/account_link`, {
    credentials: 'include',
    headers: {
      Authorization: `X-Spree-Order-Token ${window.SpreeAPI.orderToken}`,
    },
  })
    .then(response => response.ok && response.text())
    .catch(error => {
      logErrorToSentry(error);
    });
};
